// Logique de l'écran d'accueil — PURE : transforme les réponses du serveur en une
// liste ORDONNÉE de signaux. Aucun DOM, aucun réseau : c'est ce qui permet de
// tester la règle « qu'est-ce qui remonte, et dans quel ordre ».
//
// Le principe : l'écran ne montre pas tout, il montre ce qui demande une décision.
// Un tableau de 200 lignes trié par date est un dépôt de données ; il oblige à
// chercher. Ici, l'anomalie monte et le normal descend.
//
// Critère de réussite (testable, contrairement à « faire une belle app ») : ouvrir,
// compter cinq secondes, fermer — et savoir dire « tout va bien » ou « il y a ça
// qui cloche ».

/** Ordre de gravité : ce qui est le plus haut se lit en premier. */
export const GRAVITES = ["critique", "attention", "info", "ok"];

const rang = (g) => {
  const i = GRAVITES.indexOf(g);
  return i === -1 ? GRAVITES.length : i;
};

/**
 * Signal de FRAÎCHEUR — toujours en tête, avant tout chiffre d'argent.
 *
 * Raison d'être : si un canal est mort, les montants affichés en dessous sont
 * incomplets. L'utilisateur doit l'apprendre AVANT de les lire, pas après avoir
 * bâti une décision dessus. C'est pour ça que ce signal ne se range jamais
 * derrière les autres, même quand tout va bien — sa version « ok » est discrète,
 * mais présente.
 */
export function signalFraicheur(sante) {
  if (!sante || !sante.verdict) {
    return {
      id: "fraicheur",
      gravite: "attention",
      titre: "État des données inconnu",
      valeur: "—",
      detail: "Impossible de vérifier les canaux d'approvisionnement.",
      onglet: "canaux",
      epingle: true,
    };
  }
  const v = sante.verdict;
  if (v.ok) {
    return {
      id: "fraicheur",
      gravite: "ok",
      titre: "Données à jour",
      valeur: `${v.total_canaux} canaux actifs`,
      detail: "Tous les canaux d'approvisionnement rapportent normalement.",
      onglet: "canaux",
      epingle: true,
    };
  }
  const perdues = Number(v.factures_par_mois_perdues || 0);
  return {
    id: "fraicheur",
    gravite: "critique",
    titre: v.muets > 1 ? `${v.muets} canaux muets` : "1 canal muet",
    // Le nombre de canaux ne parle pas ; le volume perdu, si. « 2 canaux muets »
    // se relativise, « environ 490 factures par mois qui n'arrivent plus » non.
    valeur:
      perdues > 0
        ? `≈ ${perdues.toLocaleString("fr-FR")} factures/mois manquantes`
        : "flux interrompu",
    detail:
      "Les montants affichés ci-dessous sont incomplets tant que ces canaux n'ont pas rattrapé leur retard.",
    onglet: "canaux",
    epingle: true,
  };
}

/** Échéances à payer dans les 7 jours. Le futur seulement — voir la note plus bas. */
export function signalEcheances(stats) {
  const semaine = Number(stats?.a_venir?.semaine || 0);
  const n = Number(stats?.a_venir?.n_semaine || 0);
  if (!semaine) return null;
  return {
    id: "echeances",
    gravite: semaine > 0 ? "attention" : "info",
    titre: "À payer sous 7 jours",
    valeur: semaine,
    format: "euros",
    detail: n ? `${n} facture${n > 1 ? "s" : ""}` : "",
    onglet: "ech",
  };
}

/** Conditions d'achat qui se dégradent : remise en baisse, prix en hausse, port apparu. */
export function signalAlertes(alertes) {
  const n = Array.isArray(alertes?.rows) ? alertes.rows.length : Number(alertes?.n || 0);
  if (!n) return null;
  return {
    id: "alertes",
    gravite: n >= 5 ? "attention" : "info",
    titre: "Conditions dégradées",
    valeur: n,
    detail: `${n} article${n > 1 ? "s" : ""} dont la remise baisse ou le prix monte`,
    onglet: "alertes",
  };
}

/** Connecteurs dont les identifiants ne passent plus : ils vont se taire. */
export function signalCollecteurs(collecteurs) {
  const actifs = Array.isArray(collecteurs?.actifs) ? collecteurs.actifs : [];
  const casses = actifs.filter((c) => c.statut === "erreur_auth");
  if (!casses.length) return null;
  return {
    id: "collecteurs",
    gravite: "critique",
    titre: casses.length > 1 ? "Connexions à refaire" : "Connexion à refaire",
    valeur: casses.map((c) => c.label || c.slug).join(", "),
    // Un identifiant expiré ne casse rien tout de suite : le canal se tarit
    // simplement, et c'est exactement le mode de panne qu'on ne voit pas venir.
    detail: "Identifiants refusés — ce canal va cesser de rapporter.",
    onglet: "collecteurs",
  };
}

/**
 * Assemble et ORDONNE les signaux.
 * Le signal épinglé (fraîcheur) reste en tête quelle que soit sa gravité :
 * il conditionne la lecture de tous les autres.
 */
export function construireSignaux({ sante, stats, alertes, collecteurs } = {}) {
  const epingles = [signalFraicheur(sante)];
  const autres = [
    signalCollecteurs(collecteurs),
    signalEcheances(stats),
    signalAlertes(alertes),
  ].filter(Boolean);

  autres.sort((a, b) => rang(a.gravite) - rang(b.gravite));
  return [...epingles, ...autres];
}

/**
 * Verdict en une phrase — la réponse au test des cinq secondes.
 * Volontairement binaire : « tout va bien » ou « voici ce qui cloche ».
 */
export function verdictGlobal(signaux) {
  const critiques = signaux.filter((s) => s.gravite === "critique");
  if (critiques.length) {
    return { ton: "critique", texte: critiques.map((s) => s.titre).join(" · ") };
  }
  const attentions = signaux.filter((s) => s.gravite === "attention");
  if (attentions.length) {
    return { ton: "attention", texte: attentions.map((s) => s.titre).join(" · ") };
  }
  return { ton: "ok", texte: "Rien ne demande votre attention." };
}
